import {
  type DocumentationApi,
  type DynamicDocumentationEligibilityRequest,
  documentationApiConnector,
  type StatifiedDocumentationEligibilityRequest,
} from 'apiConnector/form/documentation';
import { DocumentationV3 } from 'epic/common-form/Documentation';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics';
import { ofType } from 'redux-observable';
import { EMPTY, type Observable } from 'rxjs';
import { catchError, groupBy, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators';
import type { Action } from 'state/actions';
import { commonFormActions } from 'state/forms/actions/form';
import { HandledWorkspace } from 'state/forms/models';
import type { Dispatchable, EpicWithThunk } from 'state/state.common';

export const FORM_DOCUMENTATION_FETCH_REQUESTED = 'FORM_DOCUMENTATION_FETCH_REQUESTED' as const;

function selectApiEndpoint(
  api: DocumentationApi,
  workspace: HandledWorkspace,
  action: ReturnType<typeof commonFormActions.formDocumentationFetchRequested>,
) {
  if (workspace === 'dynamic') {
    return api.getDynamicFormDocumentation(action.request as DynamicDocumentationEligibilityRequest);
  }

  return api.getStatifiedFormDocumentation(action.request as StatifiedDocumentationEligibilityRequest);
}

export function fetchDocumentationEpic(): EpicWithThunk {
  return (action$, state$, { httpClient, frontLogger }): Observable<Dispatchable> =>
    action$.pipe(
      ofType<
        Action,
        typeof FORM_DOCUMENTATION_FETCH_REQUESTED,
        ReturnType<typeof commonFormActions.formDocumentationFetchRequested>
      >(FORM_DOCUMENTATION_FETCH_REQUESTED),
      groupBy(action => action.negotiationId),
      mergeMap(group$ =>
        group$.pipe(
          switchMap(action => {
            const { tabId, workspace } = action;
            const tabRemoved$ = tabRemoved(action$, tabId, state$);

            const api = documentationApiConnector(httpClient);

            return selectApiEndpoint(api, workspace, action).pipe(
              takeUntil(tabRemoved$),
              map((documentation: DocumentationV3) =>
                commonFormActions.formDocumentationV3Received(workspace, tabId, documentation),
              ),
              catchError(error => {
                frontLogger.error(
                  'Documentation fetch failed for tab {tabId} in workspace {workspace} {@FrontError}',
                  tabId,
                  workspace,
                  error,
                );
                return EMPTY;
              }),
            );
          }),
        ),
      ),
    ) as Observable<Dispatchable>;
}
